import { useMemo } from 'react';
import { Dropdown, HierarchySelectList, type HierarchySelectListItem } from 'component-library';
import { useTranslation } from 'react-i18next';
import { type TFunction } from 'i18next';
import { findCountryFromCompanyId } from '@sr/utils';
import { type PartnerTreeNodeV2, type ListDropdownItem, type Country } from '@sr/types';
import { type FilterService, useLocations } from '../Locations';

type CompanyItem = {
  companyId: string;
  dwKey: string;
};

type CompanyPickerProps = {
  onChange: (item: CompanyItem) => void;
  selectedCompanyId?: string;
  filterServiceName?: FilterService;
  label?: string;
  placeholder?: string;
  errorMessage?: string;
  /**
   * Optional country to filter companies by
   */
  country?: Country;
};

export const CompanyPicker = ({
  onChange,
  selectedCompanyId,
  filterServiceName,
  label,
  placeholder,
  errorMessage,
  country,
}: CompanyPickerProps) => {
  const { partnerTrees } = useLocations({ filterServiceName, filterAdminAccess: true });

  const { t } = useTranslation();

  const companies = useMemo(() => {
    const companiesSet = new Set<string>();

    return partnerTrees.reduce<ListDropdownItem<HierarchySelectListItem>[]>((acc, tree) => {
      if (country) {
        const treeCountry = findCountryFromCompanyId(tree.item.companyId);

        if (treeCountry !== country) {
          return acc;
        }
      }

      const item = traverseTree(tree, companiesSet, t);

      if (item) {
        acc.push(item);
      }

      return acc;
    }, []);
  }, [country, partnerTrees, t]);

  return (
    <Dropdown
      items={companies}
      label={label ?? 'Company'}
      errorMessage={errorMessage}
      error={!!errorMessage}
      autoSelect="first"
      onChange={(values) => {
        const value = values[0];

        if (!value) {
          return;
        }

        const company = findRecursive(companies, value);

        if (company) {
          onChange({
            companyId: company.value,
            dwKey: company.meta?.dwKey || '',
          });
        }
      }}
      placeholder={placeholder ?? 'Select company'}
      selectedValues={selectedCompanyId ? [selectedCompanyId] : []}
    >
      <HierarchySelectList />
    </Dropdown>
  );
};

const traverseTree = (node: PartnerTreeNodeV2, companies: Set<string>, t: TFunction) => {
  const orgNr = node.item.organizationNumber;
  const companyName = node.item.name;

  if (orgNr && !companies.has(orgNr)) {
    companies.add(orgNr);
    const newItem: ListDropdownItem<HierarchySelectListItem> = {
      label: companyName,
      altLabel: `${companyName} (${t('company.picker.orgNrShort')} ${orgNr})`,
      value: orgNr,
      type: 'value',
      children: [],
      meta: { description: `${t('company.picker.orgNr')} ${orgNr}`, dwKey: node.item.dwKey },
    };

    node.children.forEach((child) => {
      const item = traverseTree(child, companies, t);

      if (item) {
        newItem.children.push(item);
      }
    });

    return newItem;
  }

  return null;
};

const findRecursive = (
  items: ListDropdownItem<HierarchySelectListItem>[],
  val: string,
): ListDropdownItem<HierarchySelectListItem> | undefined => {
  for (const item of items) {
    if (item.value === val) {
      return item;
    }

    const found = findRecursive(item.children, val);

    if (found) {
      return found;
    }
  }

  return undefined;
};
